import { useContext } from 'react'
import { generatePath } from 'react-router-dom'
import { Badge, CompanyTag } from '@/components/ui/badge'
import { GridCellProps, GridColumnProps } from '@progress/kendo-react-grid'
import { PermissionsEnum } from 'api/Identity/Types/PermissionsEnum'
import { TransactionDto, TransactionKey } from 'api/Transaction/Types/Transaction'
import { Link } from 'components/Link'
import { generateClassNameForReverseType } from 'helpers/transactions'
import { AuthSessionContext } from 'utils/AuthContext'
import { formatLocalDateUTC } from 'utils/date'
import { links } from 'utils/featureToggles'
import { isEmpty } from 'utils/is-empty'
import { hasPermission } from 'utils/permissionsValidator'
import { getCreditCreditOrFirstOneWallet } from 'utils/wallet'

import './Create/transaction.scss'

import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { GroupReferenceDto, GroupReferenceType } from 'api/Common/Types/GroupReferenceDto'
import { TransactionType } from 'api/Wallet/Types/TransactionTypeDto'
import classNames from 'classnames'
import { isTransactionPendingStatus } from 'utils/transaction/status.index'

import CountryCell from './CountryCell'

export interface GridColumn extends GridColumnProps {
  show: boolean
  width?: number
}

const ReferenceCell = (props: GridCellProps): JSX.Element => {
  const [auth] = useContext(AuthSessionContext)
  const permissions = auth?.permissions || []

  if (!hasPermission(permissions, PermissionsEnum.CanViewWalletDepositWithdrawalTransaction)) {
    return (
      <td className={generateClassNameForReverseType(props.dataItem)}>
        {props.dataItem.referenceId}
      </td>
    )
  }

  return (
    <td
      className={generateClassNameForReverseType(props.dataItem)}
      style={{ color: 'var(--link)', textDecoration: 'underline' }}
    >
      <Link
        to={{
          pathname: generatePath('/transactions/:id', {
            id: props.dataItem.id,
          }),
        }}
      >
        {props.dataItem.referenceId}
      </Link>
    </td>
  )
}

const walletDepositWithdrawalColumn: GridColumn[] = [
  {
    title: 'Reference',
    field: TransactionKey.reference,
    cell: ReferenceCell,
    show: true,
    filter: 'text',
    minResizableWidth: 180,
    width: 180,
  },
  {
    title: 'Group Reference',
    field: TransactionKey.groupReference,
    cell: (props: GridCellProps): JSX.Element => {
      if (!props.dataItem.groupReferences) {
        return <td className={generateClassNameForReverseType(props.dataItem)} />
      }
      return (
        <td className={generateClassNameForReverseType(props.dataItem)}>
          {props.dataItem.groupReferences.map((groupRef: GroupReferenceDto) => {
            const groupLink: { pathname?: string; search?: string } = {
              pathname: undefined,
              search: undefined,
            }
            const isActive = groupRef.type.id === GroupReferenceType.WithdrawBulk
            if (groupRef.type.id === GroupReferenceType.WithdrawBulk) {
              const urlSearchParams = new URLSearchParams()
              urlSearchParams.set('id', groupRef.id)
              groupLink.pathname = links.transactionBulkWithdrawals.url
              groupLink.search = `?${urlSearchParams.toString()}`
            }
            return (
              <Link to={groupLink}>
                <Tooltip>
                  <TooltipTrigger render={<span className='inline-flex' />}>
                    <Badge
                      className={classNames({ 'has-pointer': isActive })}
                      variant={isActive ? 'info' : 'neutral'}
                    >
                      {groupRef.name}
                    </Badge>
                  </TooltipTrigger>
                  <TooltipContent>{groupRef.type.name}</TooltipContent>
                </Tooltip>
              </Link>
            )
          })}
        </td>
      )
    },
    show: true,
    filter: 'text',
    minResizableWidth: 180,
    width: 180,
  },
  {
    title: 'Type',
    field: TransactionKey.type,
    show: true,
    cell: (props: GridCellProps) => (
      <td className={generateClassNameForReverseType(props.dataItem)}>
        {props.dataItem.type.name}
      </td>
    ),
    filter: 'text',
    minResizableWidth: 150,
    width: 150,
  },
  {
    title: 'Client',
    field: TransactionKey.walletTransactionWalletClient,
    cell: (props: GridCellProps): JSX.Element => {
      const data: TransactionDto = props.dataItem
      if (isEmpty(data.walletTransactions)) {
        return <td className={generateClassNameForReverseType(data)} />
      }

      const [walletTransaction] = data.walletTransactions || []
      const client = walletTransaction?.wallet?.client
      if (!client) {
        return <td className={generateClassNameForReverseType(data)} />
      }

      return (
        <td
          className={generateClassNameForReverseType(data)}
          style={{ color: 'var(--link)', textDecoration: 'underline' }}
        >
          <Link
            state={{ redirectTo: links.transactionDepositWithdrawal.url }}
            to={{
              pathname: generatePath(links.recordsClients.detailsUrl as string, {
                id: client.id,
                slug: 'details',
              }),
            }}
          >
            {client.name}
          </Link>
        </td>
      )
    },
    show: true,
    filter: 'text',
    minResizableWidth: 250,
    width: 250,
  },
  {
    title: 'Wallet',
    field: TransactionKey.walletTransactionWallet,
    cell: (props: GridCellProps): JSX.Element => {
      const data: TransactionDto = props.dataItem
      if (isEmpty(data.walletTransactions)) {
        return <td className={generateClassNameForReverseType(data)} />
      }

      const [walletTransaction] = data.walletTransactions || []
      const wallet = walletTransaction?.wallet
      if (!wallet) {
        return <td className={generateClassNameForReverseType(data)} />
      }

      return (
        <td
          className={generateClassNameForReverseType(data)}
          style={{ color: 'var(--link)', textDecoration: 'underline' }}
        >
          <Link
            state={{ redirectTo: links.transactionDepositWithdrawal.url }}
            to={{
              pathname: generatePath(links.recordsWallets.detailsUrl as string, {
                id: wallet.id,
                slug: 'details',
              }),
            }}
          >
            {wallet.name}
          </Link>
        </td>
      )
    },
    resizable: true,
    show: true,
    filter: 'text',
    minResizableWidth: 150,
    width: 150,
  },
  {
    title: 'Amount',
    field: TransactionKey.walletTransactionAmount,
    cell: (props: GridCellProps): JSX.Element => {
      const data: TransactionDto = props.dataItem
      if (isEmpty(data.walletTransactions)) {
        return <td className={generateClassNameForReverseType(data)} />
      }

      const [walletTransaction] = data?.walletTransactions || []
      const amount = walletTransaction?.amount?.toFixed(2)

      return <td className={generateClassNameForReverseType(props.dataItem, 'number')}>{amount}</td>
    },
    resizable: true,
    show: true,
    filter: 'text',
    minResizableWidth: 90,
    width: 90,
  },
  {
    title: 'Currency',
    field: TransactionKey.walletTransactionWalletCurrency,
    cell: (props: GridCellProps): JSX.Element => {
      const data: TransactionDto = props.dataItem
      if (isEmpty(props.dataItem.walletTransactions)) {
        return <td className={generateClassNameForReverseType(props.dataItem)} />
      }

      return (
        <td className={generateClassNameForReverseType(props.dataItem)}>
          {getCreditCreditOrFirstOneWallet(data.walletTransactions)?.wallet.currency.id}
        </td>
      )
    },
    resizable: true,
    show: true,
    filter: 'text',
    minResizableWidth: 100,
    width: 100,
  },
  {
    title: 'Payment Provider',
    field: TransactionKey.walletTransactionPaymentProviderTransaction,
    cell: (props: GridCellProps): JSX.Element => {
      if (isEmpty(props.dataItem.walletTransactions)) {
        return <td className={generateClassNameForReverseType(props.dataItem)} />
      }

      const [walletTransaction] = props.dataItem.walletTransactions || []
      const paymentProviderName =
        walletTransaction?.paymentProviderTransaction?.paymentProvider.name
      const paymentProviderExternalName =
        walletTransaction?.paymentProviderTransaction?.paymentProvider.externalName

      if (paymentProviderExternalName) {
        return (
          <td className={generateClassNameForReverseType(props.dataItem)}>
            <Tooltip key='top'>
              <TooltipTrigger render={<span />}>{paymentProviderName}</TooltipTrigger>
              <TooltipContent side='top'>{paymentProviderExternalName}</TooltipContent>
            </Tooltip>
          </td>
        )
      }

      return (
        <td className={generateClassNameForReverseType(props.dataItem)}>{paymentProviderName}</td>
      )
    },
    show: true,
    filter: 'text',
    minResizableWidth: 250,
    width: 250,
  },
  {
    title: 'Wallet Type',
    field: 'Wallet Type',
    cell: (props: GridCellProps): JSX.Element => {
      const walletTransactions = props.dataItem.walletTransactions
      if (isEmpty(walletTransactions)) {
        return <td className={generateClassNameForReverseType(props.dataItem)} />
      }
      const walletDebitTransaction = getCreditCreditOrFirstOneWallet(walletTransactions)
      const wallet = walletDebitTransaction?.wallet
      if (!wallet) {
        return <td className={generateClassNameForReverseType(props.dataItem)} />
      }

      return <td>{wallet.walletType.name}</td>
    },
    show: true,
    resizable: true,
    minResizableWidth: 250,
    width: 250,
    filter: 'text',
  },
  {
    title: 'Status',
    field: TransactionKey.state,
    cell: (props: GridCellProps): JSX.Element => {
      const status = props.dataItem.state
      const transactionType = props.dataItem.type

      if (isEmpty(status)) {
        return <td className={generateClassNameForReverseType(props.dataItem)} />
      }
      if (
        isTransactionPendingStatus(status.id) &&
        transactionType.id === TransactionType.WalletWithdrawal
      ) {
        return (
          <td
            className={generateClassNameForReverseType(props.dataItem)}
          >{`${status.name} on PSP side`}</td>
        )
      }

      return <td className={generateClassNameForReverseType(props.dataItem)}>{status.name}</td>
    },
    show: true,
    filter: 'text',
    minResizableWidth: 140,
    width: 140,
  },
  {
    title: 'Entity',
    field: TransactionKey.walletTransactionWalletCompany,
    cell: (props: GridCellProps): JSX.Element => {
      if (isEmpty(props.dataItem.walletTransactions)) {
        return <td className={generateClassNameForReverseType(props.dataItem)} />
      }

      const [walletTransaction] = props.dataItem.walletTransactions || []
      const company = walletTransaction?.wallet?.company
      if (!company) {
        return <td className={generateClassNameForReverseType(props.dataItem)} />
      }

      return (
        <td className={generateClassNameForReverseType(props.dataItem)}>
          <CompanyTag id={company.id}>{company.name}</CompanyTag>
        </td>
      )
    },
    resizable: true,
    show: true,
    filter: 'text',
    width: 80,
    minResizableWidth: 80,
  },
  {
    title: 'Country',
    field: TransactionKey.walletTransactionPaymentProviderTransactionUserCountry,
    cell: CountryCell,
    show: true,
    filter: 'text',
    minResizableWidth: 120,
    width: 120,
  },
  {
    title: 'Date Created',
    field: TransactionKey.createdDate,
    cell: (props: GridCellProps) => (
      <td className={generateClassNameForReverseType(props.dataItem, 'date')}>
        {formatLocalDateUTC(props.dataItem.createdDate, true)}
      </td>
    ),
    show: true,
    resizable: true,
    filter: 'text',
    minResizableWidth: 150,
    width: 150,
  },
  {
    title: 'Date Updated',
    field: TransactionKey.updatedDate,
    cell: (props: GridCellProps) => (
      <td className={generateClassNameForReverseType(props.dataItem, 'date')}>
        {formatLocalDateUTC(props.dataItem.updatedDate, true)}
      </td>
    ),
    show: true,
    resizable: true,
    filter: 'text',
    minResizableWidth: 150,
    width: 150,
  },
]

export default walletDepositWithdrawalColumn
